import { Box, Button, FormField, Input, SpaceBetween, TokenGroup } from '@amzn/awsui-components-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditEventActions } from '../../store/edit-event.context';
import { i18nKeys } from '../../utils/i18n.utils';
import { isValidDomainSuffix, splitDelimitedEmails } from '../../utils/string.utils';
import { EditCampaignActions } from '../../store/edit-campaign.context';
import { DEFAULT_EMAIL_DOMAINS } from '@/src/constants/shared/default-email-domains';

interface DomainListProps {
  label: string;
  description?: string;
  addDomain: (domains: string[], newDomain: string, eventAction: EditEventActions | EditCampaignActions) => void;
  removeDomain: (
    domains: string[],
    removeDomainIndex: number,
    eventAction: EditEventActions | EditCampaignActions
  ) => void;
  domains: string[];
  eventAction: EditEventActions | EditCampaignActions;
  disabled?: boolean;
  'data-testid'?: string;
}

export const DomainList: React.FC<DomainListProps> = ({
  label,
  description,
  addDomain,
  removeDomain,
  domains,
  eventAction,
  disabled = false,
  ...rest
}) => {
  const [newDomain, setNewDomain] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { t } = useTranslation();

  const validateAndSplitDomains = (newDomains: string): string[] => {
    const domainList: string[] = splitDelimitedEmails(newDomains);
    const invalidDomains = (domainList || []).filter((e) => !isValidDomainSuffix(e));
    if (invalidDomains.length > 0) {
      setErrorMessage(t(i18nKeys.errors.eventPermissions.invalidDomain) + invalidDomains.join(', '));
    } else {
      setErrorMessage('');
      return domainList;
    }
    return [];
  };

  const handleAddDomain = (selectedDomain: string) => {
    const domainList = validateAndSplitDomains(selectedDomain);
    if (domainList.length > 0) {
      domainList.forEach((domain) => {
        if (!domain.startsWith('@')) domain = '@' + domain;
        const domainAlreadyExists = domains.includes(domain);
        if (!domainAlreadyExists) {
          addDomain(domains, domain, eventAction);
        } else {
          setErrorMessage(t(i18nKeys.errors.eventPermissions.existingDomain));
        }
      });
    }
  };

  const onAddAmazonDomains = () => {
    DEFAULT_EMAIL_DOMAINS.forEach((domain) => handleAddDomain(domain));
    setNewDomain('');
  };

  return (
    <React.Fragment>
      <SpaceBetween direction="vertical" size="s">
        <FormField label={label} description={description} data-testid={rest['data-testid'] || null}>
          <SpaceBetween direction="horizontal" size="s">
            <Input
              data-testid="domain-input"
              placeholder="@example.com"
              disabled={disabled}
              value={newDomain}
              onChange={({ detail }) => setNewDomain(detail.value)}
              onKeyDown={({ detail }) => {
                // 13 is the keyCode for "Enter"
                if (detail.keyCode === 13) {
                  handleAddDomain(newDomain);
                  setNewDomain('');
                }
              }}
            />
            <Button
              disabled={disabled}
              iconName="add-plus"
              onClick={() => {
                handleAddDomain(newDomain);
                setNewDomain('');
              }}
              data-testid="add-domain-btn"
            >
              {t(i18nKeys.events.eventDetails.buttons.addDomain)}
            </Button>
            <Button
              disabled={disabled}
              iconName="add-plus"
              variant="primary"
              onClick={onAddAmazonDomains}
              data-testid="add-amazon-domains-btn"
            >
              {t(i18nKeys.events.eventDetails.buttons.addAmazonDomains)}
            </Button>
            <Box color="text-status-error" variant="div">
              {errorMessage}
            </Box>
          </SpaceBetween>
        </FormField>
        {!domains ||
          (domains && domains.length < 1 && (
            <div style={{ display: 'block' }}>
              <i>{t(i18nKeys.events.eventDetails.messages.thisListIsEmpty)}</i>
            </div>
          ))}
        {
          <TokenGroup
            items={domains.map((domain) => ({ label: domain, dismissLabel: `Remove ${domain}` })) || []}
            onDismiss={({ detail: { itemIndex } }) => {
              removeDomain(domains, itemIndex, eventAction);
            }}
          />
        }
      </SpaceBetween>
    </React.Fragment>
  );
};
